import type { FC, ReactNode } from 'react';
import React from 'react';
import { flattenTree, Icon, type TreeNode, type TreeViewRow } from 'venn-ui-kit';
import { cn } from '@/lib/utils';

interface DepthTreeProps<T> {
  root: T;
  maxDepth?: number;
  shouldRenderRoot?: boolean;
  filteredItemsIds?: string[];
  getKey: (item: T) => string;
  getChildren: (item: T) => T[];
  getLabel: (item: T) => ReactNode;
  customLastLabel?: ReactNode;
  isCollapsed?: boolean;
}

interface TreeNodeWithDownLeafs<T> extends TreeNode<T> {
  downLeafs: boolean[];
}

const toTreeNode = <T,>(
  node: T,
  downLeafs: boolean[],
  hasNext: boolean,
  getKey: (item: T) => string,
  getChildren: (item: T) => T[],
): TreeNodeWithDownLeafs<T> => ({
  data: node,
  downLeafs: [...downLeafs, hasNext],
  key: getKey(node),
  children:
    (getChildren(node) ?? []).map((c, index, array) =>
      toTreeNode(c, [...downLeafs, hasNext], index < array.length - 1, getKey, getChildren),
    ) ?? [],
  selectedOverride: undefined,
});

const PrefixRowTree: FC<{ row: TreeNodeWithDownLeafs<never>; isTreeFlat: boolean; isLastRow: boolean }> = ({
  row,
  isTreeFlat = false,
  isLastRow,
}) => {
  const lastColHasFollowers = row.downLeafs[row.downLeafs.length - 1];
  const isRootBranch = row.downLeafs.length === 2;
  const spacerColumns = row.downLeafs.slice(2, -1).map((hasFollowers, depth) => ({
    hasFollowers,
    key: row.downLeafs.slice(0, depth + 3).join(),
  }));
  return (
    <div className="flex">
      <span className="ml-[3px]" />
      {!isRootBranch && !isTreeFlat && (
        <>
          {row.downLeafs[1] && <SpacerNode />}
          {!row.downLeafs[1] && <span className="mr-px" />}
          <span className="ml-[24px]" />
          {spacerColumns.map(({ hasFollowers, key }) => (
            <React.Fragment key={key}>
              {hasFollowers && <SpacerNode />}
              {!hasFollowers && <span className="mr-px" />}
              <span className="ml-[27px]" />
            </React.Fragment>
          ))}
        </>
      )}
      <NodeIcon isLast={isTreeFlat ? isLastRow : !lastColHasFollowers} />
    </div>
  );
};

export const DepthTreeView = <T,>({
  root,
  maxDepth,
  shouldRenderRoot = true,
  filteredItemsIds,
  getKey,
  getChildren,
  getLabel,
  customLastLabel,
  isCollapsed = false,
}: DepthTreeProps<T>) => {
  const isTreeFiltered = !!filteredItemsIds?.length;
  const tree = toTreeNode(root, [], false, getKey, getChildren);
  let rows = flattenTree(tree);

  if (maxDepth !== undefined) {
    rows = rows.filter((r) => r.level <= maxDepth);
  }

  if (isTreeFiltered) {
    rows = rows.filter((r) => filteredItemsIds.includes(r.key));
  }

  const shouldRenderChildren = rows.length > 1;

  return (
    <>
      {shouldRenderRoot && (
        <div>
          <Icon className="text-venn-deep-dark-blue" type="folders" />
          <span className="ml-[9px]">{rows[0] && getLabel(rows[0].data)}</span>
        </div>
      )}
      {((isTreeFiltered || shouldRenderChildren) && !isCollapsed) || customLastLabel ? <RootNode /> : null}
      <div
        className={cn([
          'transition-all duration-300 ease-in-out',
          isCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'opacity-100',
        ])}
      >
        {(isTreeFiltered || shouldRenderChildren) &&
          rows.slice(isTreeFiltered ? 0 : 1).map((row, index) => {
            const isLastRow = !customLastLabel && index === rows.length - 1;
            return renderTreeNode<T>(
              row,
              getLabel(row.data),
              isTreeFiltered,
              isLastRow,
              row.key ? `node-${row.key}` : `row-${index}`,
            );
          })}
      </div>
      {customLastLabel ? renderCustomTreeNode(customLastLabel) : null}
    </>
  );
};

const NodeIcon = ({ isLast = false }) => (
  <div
    className={cn([
      'relative w-[16px]',
      "before:block before:content-[''] before:absolute before:left-0 before:top-0 before:bg-venn-dark-blue before:w-px",
      "after:block after:content-[''] after:absolute after:left-0 after:w-[16px] after:h-px after:bg-venn-dark-blue",
      isLast ? 'h-[9px] before:h-[9px] after:bottom-0' : 'h-[18px] before:h-[18px] after:bottom-[9px]',
    ])}
  >
    <Icon
      type="diamond"
      className={cn(
        'absolute right-0 -mr-[3px] mb-0 text-[6px] text-venn-deep-dark-blue not-italic leading-[0]',
        isLast ? 'bottom-0' : 'bottom-[9px]',
      )}
    />
  </div>
);

const SpacerNode = () => (
  <div>
    <div className="w-px min-w-px h-full bg-venn-dark-blue" />
  </div>
);
const RootNode = () => (
  <div className="w-px min-w-px h-[5px] bg-venn-dark-blue -mt-[3px] ml-[3px] -translate-y-[2px] scale-y-[2px]" />
);

const getDummyNode = <T,>() =>
  toTreeNode<T>(
    {} as unknown as T,
    [],
    false,
    () => '',
    () => [],
  ) as unknown as TreeViewRow<T>;

const renderTreeNode = <T,>(
  row: TreeViewRow<T>,
  label: ReactNode,
  isTreeFlat: boolean,
  isLastRow: boolean,
  rowKey?: React.Key,
) => (
  <div key={rowKey} className="flex h-[18px] gap-[6px]">
    <PrefixRowTree row={row as unknown as TreeNodeWithDownLeafs<never>} isTreeFlat={isTreeFlat} isLastRow={isLastRow} />
    <span className="text-[14px] text-venn-grey-80 ml-[3px] truncate max-w-[220px]">{label}</span>
  </div>
);

const renderCustomTreeNode = <T,>(label: ReactNode) => (
  <div className="mt-0">{renderTreeNode<T>(getDummyNode<T>(), label, true, true)}</div>
);
